import React, { useCallback, useEffect, useState, memo } from 'react';
import { connect } from 'react-redux';
import { Map, fromJS, List } from 'immutable';

import connectOptions, { mergeProps } from '../../utils/connectOptions';
import CountryDropDown from '../CountryDropDown';
import {
  handleGetSuggestion,
  handleUpdateParentCompany,
  closeValidationError,
  changeField,
  saveCompany,
  findHarvcos,
  findAnalyst,
} from '../../actions/company/buyerCreate';

import Popups from '../../components/Popup';
import AutoCompleteContainer from '../AutoComplete';
import { getChanged, unwrap, getIn } from '../../utils/ChangeSpy';
import { checkPhone, formatPhone } from '../services/phoneRules';
import checkZip from '../services/zipRules';
import { PrettyErrorProvider } from '../../contexts/PrettyErrorContext';
import { useRedirectForceContext } from '../../contexts/RedirectForceContext';
import { usePopupContext } from '../../contexts/PopupContext';

const handleGetSuggestionValue = suggestion => suggestion.legalName;
const handleRenderSuggestion = suggestion => <div>{suggestion.legalName}</div>;

const getBusinessModelIds = models => {
  const result = [];

  models.forEach(model => {
    if (model.get('checked')) {
      result.push(model.get('id'));
    }
  });

  return result;
};

const getIndustryTagIds = tags => {
  const result = [];

  tags.forEach(tag => {
    if (tag.get('checked')) {
      result.push(tag.get('id'));
    }
    tag.get('children').forEach(child => {
      if (child.get('checked')) {
        result.push(child.get('id'));
      }
    });
  });

  return result;
};

const initialFormError = { phone: '', zip: '', industryCategory: '', businessModel: '' };

const AddCompanyBuyerPopupContainer = ({
  children,
  company,
  states,
  entityTypes,
  isValid,
  businessModels,
  tags,
  inputErrors,
  user,
  popup,
  changeField,
  saveCompany,
  closeValidationError,
  findHarvcos,
  findAnalyst,
  handleGetSuggestion,
  handleUpdateParentCompany,
}) => {
  const redirectForce = useRedirectForceContext();
  const { openPopup } = usePopupContext();
  const [formError, setFormError] = useState(initialFormError);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    changeField({ name: 'harvcoName', value: user.get('module') || '' });
    changeField({ name: 'buyerHarvcoLeadId', value: user.get('moduleId') || null });
  }, []);

  const handleClearFormError = useCallback(() => {
    setFormError(initialFormError);
  }, []);

  const showPhoneError = useCallback(() => {
    setFormError(prev => ({
      ...prev,
      phone:
        'Phone must be a valid North American area code and format (###-###-####) or +[numbers, spaces and dashes] if international',
    }));
  }, []);

  const showZipError = useCallback(() => {
    setFormError(prev => ({
      ...prev,
      zip:
        'Zip code should follow U.S. (XXXXX or XXXXX-XXXX) or Canadian (A1A 1A1) format; otherwise, please set address to International ("...")',
    }));
  }, []);

  const isValidPhone = useCallback(
    phone => {
      const phoneValue = phone || '';

      if (company.get('showCountry', false)) return true;

      if (phoneValue && !checkPhone(phoneValue)) {
        showPhoneError();

        return false;
      }

      return true;
    },
    [company, showPhoneError],
  );

  const isValidZip = useCallback(
    zip => {
      if (!checkZip(zip)) {
        showZipError();

        return false;
      }

      return true;
    },
    [showZipError],
  );

  const isValidIndustryCategory = useCallback((ids, businessModelIds) => {
    let result = true;

    if (!ids.length) {
      setFormError(prev => ({ ...prev, industryCategory: 'Industry Tags is required' }));
      result = false;
    }

    if (!businessModelIds.length) {
      setFormError(prev => ({ ...prev, businessModel: 'Business Model is required' }));
      result = false;
    }

    return result;
  }, []);

  const handleChange = useCallback(
    event => {
      const {
        target: { name, value },
      } = event;

      if (!name || name === 'parentCompany') return;

      if (name === 'companyType') {
        const typeHasParent = ['DivPri', 'DivLBO', 'DivPub', 'DivVC', 'DivNFP', 'DivFor'];

        if (typeHasParent.indexOf(value) === -1) {
          handleUpdateParentCompany({ text: '', selected: null });
          changeField({ name: 'parentId', value: null });
        } else {
          changeField({ name: 'parentId', value: 0 });
        }
      }
      changeField({ name, value });
    },
    [changeField, handleUpdateParentCompany],
  );

  const handleCreate = useCallback(
    event => {
      event.preventDefault();

      if (submitting) return;

      const phone = unwrap(company.get('phone', ''));
      const zip = unwrap(company.get('zip', ''));
      const industryCategoryIds = getIndustryTagIds(tags);
      const businessModelIds = getBusinessModelIds(businessModels);

      if (isValidPhone(phone) && isValidZip(zip) && isValidIndustryCategory(industryCategoryIds, businessModelIds)) {
        handleClearFormError();

        const body = getChanged(company);

        if (body.country && body.country.countryId) {
          body.countryId = body.country.countryId;
          delete body.country;
        }

        const afterSuccess = ({ response }) => {
          setTimeout(() => {
            redirectForce(`/company/${response.id}`);
          }, 0);
        };

        const afterError = () => {
          setSubmitting(false);
        };

        setSubmitting(true);
        saveCompany({
          body: {
            ...body,
            parentId: body.parentId || null,
            industryCategoryIds: industryCategoryIds.concat(businessModelIds),
          },
          afterSuccess,
          afterError,
        });
      }
    },
    [
      submitting,
      company,
      tags,
      businessModels,
      isValidPhone,
      isValidZip,
      isValidIndustryCategory,
      handleClearFormError,
      saveCompany,
      redirectForce,
    ],
  );

  const handleSuggestChange = useCallback(
    ({ name, value }) => {
      changeField({ name, value });
    },
    [changeField],
  );

  const handleToggleCountry = useCallback(() => {
    changeField({ name: 'showCountry', value: !company.get('showCountry') });
  }, [changeField, company]);

  const handleParentCompanyClick = useCallback(() => {
    const selectedCompany = company.getIn(['parentCompany', 'selected']);

    if (selectedCompany) {
      window.open(`/company/${selectedCompany.get('id')}`);
    }
  }, [company]);

  const handleUpdateSuggestions = useCallback(
    update => {
      handleUpdateParentCompany(update);
    },
    [handleUpdateParentCompany],
  );

  const handleSuggestionFetch = useCallback(
    ({ value }) => {
      handleGetSuggestion(value);
    },
    [handleGetSuggestion],
  );

  const handleSuggestionSelected = useCallback(
    (event, { suggestion }) => {
      handleUpdateParentCompany({
        text: suggestion.legalName,
        selected: suggestion,
        suggestions: [],
      });
    },
    [handleUpdateParentCompany],
  );

  const handleChangeCountryName = useCallback(
    event => {
      changeField({ name: 'country.countryName', value: event.target.value });
    },
    [changeField],
  );

  const handleSelectCountry = useCallback(
    selection => {
      changeField({ name: 'country.countryId', value: selection.id });
      changeField({ name: 'country.countryName', value: selection.text });
    },
    [changeField],
  );

  const handleBlur = useCallback(
    ({ target: { name, value = '' } }) => {
      if (company.get('showCountry', false)) {
        handleClearFormError();

        return;
      }

      if (name === 'phone') {
        if (isValidPhone(value)) {
          handleClearFormError();
          changeField({ name: 'phone', value: value ? formatPhone(value) : '' });
        } else {
          showPhoneError();
        }
      } else if (name === 'zip' && !checkZip(value)) {
        showZipError();
      }
    },
    [company, changeField, handleClearFormError, isValidPhone, showPhoneError, showZipError],
  );

  const handleErrorClose = useCallback(
    (event, field) => {
      if (field === 'zip') {
        setFormError(prev => ({ ...prev, zip: '' }));
      } else {
        closeValidationError({ field });
      }
    },
    [closeValidationError],
  );

  const handleBICClick = useCallback(() => {
    openPopup('TagsManagementPopup', {
      addNew: true,
      clientView: true,
      cb: () =>
        openPopup('AddBuyerCompanyPopup', {
          cb: popup.getIn(['props', 'cb']),
        }),
    });
  }, [openPopup, popup]);

  const suggestHarvco = (
    <AutoCompleteContainer
      idName="buyerHarvcoLeadId"
      inputProps={{ name: 'harvcoName' }}
      name="harvcoName"
      onChange={handleSuggestChange}
      onFind={options => findHarvcos({ ...options, field: 'harvcoName' })}
      rootPath={['targetCompany', 'buyerCreate']}
      suggestionsName="harvcoSuggests"
      valueName="harvcoName"
    />
  );

  const suggestAnalyst = (
    <AutoCompleteContainer
      idName="recordSubOwnerId"
      inputProps={{ name: 'analystName' }}
      name="analystName"
      onChange={handleSuggestChange}
      onFind={options => findAnalyst({ ...options, field: 'analystName' })}
      rootPath={['targetCompany', 'buyerCreate']}
      suggestionsName="analystSuggests"
      valueName="analystName"
    />
  );

  const suggestions = company.getIn(['parentCompany', 'suggestions'], fromJS([]));
  const text = company.getIn(['parentCompany', 'text']) || '';
  const suggestionProps = {
    name: 'parentCompany',
    inputProps: {
      className: 'parentCompany form-control',
      id: 'parentCompany',
      name: 'parentCompany',
      disabled: false,
    },
    suggestions,
    onUpdateSuggestions: handleUpdateSuggestions,
    getSuggestion: handleSuggestionFetch,
    getSuggestionValue: handleGetSuggestionValue,
    renderSuggestion: handleRenderSuggestion,
    onSuggestionSelected: handleSuggestionSelected,
    checkOn: 'text',
    text,
  };

  const countryComponent = (
    <CountryDropDown
      name="country.countryId"
      onChange={handleChangeCountryName}
      onSelect={handleSelectCountry}
      value={company.getIn(['country', 'countryName'])}
    />
  );

  return (
    <PrettyErrorProvider inputErrors={inputErrors} onErrorClose={handleErrorClose}>
      <>
        <Popups.AddBuyerCompanyPopup
          businessModels={businessModels}
          company={company}
          countryComponent={countryComponent}
          entityTypes={entityTypes}
          error={formError}
          isValid={isValid}
          onBICClick={handleBICClick}
          onBlur={handleBlur}
          onChange={handleChange}
          onCreate={handleCreate}
          onErrorClose={handleClearFormError}
          onParentCompanyClick={handleParentCompanyClick}
          onToggleCountry={handleToggleCountry}
          states={states}
          submitting={submitting}
          suggestAnalyst={suggestAnalyst}
          suggestHarvco={suggestHarvco}
          suggestionProps={suggestionProps}
          tags={tags}
        />
        {children}
      </>
    </PrettyErrorProvider>
  );
};

const defaultErrors = Map();

function mapStateToProps(state, props) {
  return {
    ...props,
    businessModels: unwrap(state.businessModels.get('models', List())),
    tags: getIn(state.industryTags, 'tags', List()),
    inputErrors: state.targetCompany.buyerCreate.get('inputErrors', defaultErrors),
    isValid: state.targetCompany.buyerCreate.get('isValid', true),
    states: state.targetCompany.buyerCreate.get('states'),
    entityTypes: state.targetCompany.buyerCreate.get('entityTypes'),
    company: state.targetCompany.buyerCreate,
    buyerCompany: state.targetCompany.buyerCreate,
    target: state.targetCompany.target,
    user: state.auth.get('user', null),
  };
}

export { AddCompanyBuyerPopupContainer };

const Export = connect(
  mapStateToProps,
  {
    handleGetSuggestion,
    changeField,
    saveCompany,
    closeValidationError,
    findHarvcos,
    findAnalyst,
    handleUpdateParentCompany,
  },
  mergeProps,
  connectOptions,
)(memo(AddCompanyBuyerPopupContainer));

Export.closable = false;

export default Export;
